import QuillDelta from 'quill-delta';

import * as graphql from '@treams/treams-network';

import {
	FeedbackQuestionCardItem,
	FeedbackQuestionCardSaveValues,
} from '../../../../../feedback/settings/FeedbackQuestionCard';

export type ComposerQuestion = graphql.FeedbackRequestQuestionInput & { id: string };

type Localize = <T extends { languageID: string }>(items?: T[] | null, defaultValue?: T) => T;

// Builds the card item contract from a composer draft; `category` is stubbed
// since composer drafts are never persisted to the shared library.
export function toFeedbackQuestion(
	question: ComposerQuestion,
	languageID: string,
): FeedbackQuestionCardItem {
	const text: graphql.LocalizedString = question.localizedText ?? [
		{ languageID, text: question.text ?? '' },
	];

	const subTitle: graphql.LocalizedString = [{ languageID, text: question.subTitle ?? '' }];

	const additionalDetails: graphql.LocalizedAttributedString = question.additionalDetails
		? [{ languageID, text: new QuillDelta(question.additionalDetails) }]
		: [];

	const options = (question.options ?? []).map((option) => ({
		id: option.id ?? '',
		text: option.text,
	}));

	return {
		id: question.id,
		text,
		type: question.type,
		subTitle,
		additionalDetails,
		options,
		category: { id: question.categoryID ?? '' },
	};
}

/**
 * Map the card's edited values back onto a composer draft question, keeping the
 * ad-hoc identifiers and relationship fields intact and down-converting the
 * localized values to the composer's single-language shape.
 */
export function fromCardValues(
	values: FeedbackQuestionCardSaveValues,
	previous: ComposerQuestion,
	localize: Localize,
): ComposerQuestion {
	const localizedAdditionalDetails = localize(values.additionalDetails)?.text;

	return {
		...previous,
		type: values.type,
		text: localize(values.text)?.text ?? '',
		localizedText: values.text,
		subTitle: localize(values.subTitle)?.text ?? '',
		additionalDetails: localizedAdditionalDetails
			? new QuillDelta(localizedAdditionalDetails)
			: undefined,
		options: values.options.map((option) => ({ id: option.id, text: option.text })),
	};
}
